import React, { useEffect, useState } from "react";
import { Loader2, Save, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminGet, adminPut, formatApiError } from "../../lib/api";

/**
 * Generic editor that fetches /admin/{resource}, renders a form built from a schema,
 * and submits PUT /admin/{resource}.
 */
const SingletonEditor = ({ resource, title, eyebrow, fields, testIdPrefix }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await adminGet(`/${resource}`);
        setData(d);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [resource]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await adminPut(`/${resource}`, data);
      setData(saved);
      setSavedAt(new Date());
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  return (
    <form onSubmit={onSave} data-testid={`${testIdPrefix}-form`} className="space-y-8">
      <header>
        <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">{eyebrow}</div>
        <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">{title}</h1>
      </header>

      <div className="bg-white border border-gray-200 p-6 lg:p-8 space-y-5">
        {fields.map((f) => (
          <Field key={f.key} field={f} value={data?.[f.key] ?? ""} onChange={(v) => update(f.key, v)} testId={`${testIdPrefix}-${f.key}`} />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-[#0A192F]/50">
          {savedAt && (
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#D4AF37]" /> Last saved {savedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid={`${testIdPrefix}-save`}
          className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-8 py-4 font-display text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

const Field = ({ field, value, onChange, testId }) => {
  const { key, label, type = "text", help, rows } = field;
  if (type === "textarea") {
    return (
      <div>
        <Label>{label}</Label>
        <textarea
          rows={rows || 4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none resize-y"
        />
        {help && <p className="mt-1 text-xs text-[#0A192F]/50">{help}</p>}
      </div>
    );
  }
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
      />
      {help && <p className="mt-1 text-xs text-[#0A192F]/50">{help}</p>}
    </div>
  );
};

const Label = ({ children }) => (
  <label className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/70 font-semibold">
    {children}
  </label>
);

export default SingletonEditor;
